import React from 'react';

/*
 * Status/priority tags only — never a click target. Deliberately a
 * squared-off, bordered shape (not a filled pill) so it reads as an
 * official/institutional label, visually distinct from Button's solid
 * rounded-rectangle shape. Mixing the two shapes into the same
 * soft-color-pill language was the #1 source of "is this clickable?"
 * confusion in the portal (report cards had status pills and action
 * pills that looked identical).
 */

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'purple';

const TONE_STYLES: Record<BadgeTone, string> = {
  neutral: 'border border-gray-400 text-gray-700 dark:border-gray-500 dark:text-gray-300',
  info: 'border border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300',
  success: 'border border-emerald-600 text-emerald-700 dark:border-emerald-500 dark:text-emerald-400',
  warning: 'border border-accent-600 text-accent-800 dark:border-accent-500 dark:text-accent-400',
  danger: 'border border-red-600 text-red-700 dark:border-red-500 dark:text-red-400',
  purple: 'border border-purple-600 text-purple-700 dark:border-purple-500 dark:text-purple-400',
};

// Status/priority values are inconsistent across the API (snake_case,
// mixed case) — this keeps every badge's color mapping in one place
// instead of each page reinventing its own switch statement.
const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warning',
  open: 'warning',
  assigned: 'info',
  in_progress: 'info',
  resolved: 'success',
  closed: 'neutral',
  duplicate: 'neutral',
  rejected: 'danger',
  low: 'success',
  medium: 'warning',
  high: 'warning',
  urgent: 'danger',
  critical: 'danger',
  active: 'success',
  inactive: 'neutral',
  suspended: 'danger',
  trial: 'info',
  cancelled: 'neutral',
  draft: 'neutral',
  issued: 'info',
  paid: 'success',
  void: 'danger',
};

export const toneForValue = (value: string): BadgeTone => STATUS_TONE[value?.toLowerCase?.()] ?? 'neutral';

const formatLabel = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

interface BadgeProps {
  children?: React.ReactNode;
  tone?: BadgeTone;
  /** Convenience: pass a raw status/priority string and skip choosing a tone + formatting it. */
  value?: string;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({ children, tone, value, className = '' }) => {
  const resolvedTone = tone ?? (value ? toneForValue(value) : 'neutral');
  return (
    <span
      className={`inline-flex items-center rounded-sm bg-white px-2 py-0.5 text-[11px] font-medium dark:bg-gray-900 ${TONE_STYLES[resolvedTone]} ${className}`}
    >
      {children ?? (value ? formatLabel(value) : null)}
    </span>
  );
};

export default Badge;
